import { useState, useEffect, type ReactNode } from 'react';
import { FeaturedEventCard } from '@/components/common/FeaturedEventCard';
import { EventCard } from './components/EventCard';
import { MemberRegistrationConfirmedDialog } from './components/MemberRegistrationConfirmedDialog';
import { useFeaturedEvent, useRegisterForEvent, useRegisteredEvents, useUpcomingEvents, useSearchEvents } from '@/hooks/useEvents';
import { usePageAccumulation } from '@/hooks/usePageAccumulation';
import { toFeaturedEventProps, toEventCardProps } from './event.mappers';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { Skeleton } from '@/components/ui/Skeleton';
import { ErrorState } from '@/components/ui/ErrorState';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Checkbox } from '@/components/ui/Checkbox';
import { PageHeader } from '@/components/common/PageHeader';
import { SearchIcon } from '@/assets/icons/SearchIcon';
import { eventDetailPath } from '@/lib/constants/routes';
import type { EventCardDto } from '@/lib/types/events.types';

/** Green count pill that appears beside section headings — matches Figma exactly. */
function CountPill({ count }: { count: number }) {
  return (
    <span className="inline-flex h-6 min-w-6 items-center justify-center rounded-full bg-green px-1.5 text-[0.75rem] font-semibold text-white">
      {count}
    </span>
  );
}

function SectionHeading({ children, count }: { children: ReactNode; count?: number }) {
  return (
    <div className="flex items-center gap-3 mb-5">
      <h2 className="text-[1.125rem] font-bold text-ink">{children}</h2>
      {count !== undefined && <CountPill count={count} />}
    </div>
  );
}

function EventGridSkeleton() {
  return (
    <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
      {Array.from({ length: 3 }).map((_, i) => (
        <div
          key={i}
          className="flex flex-col overflow-hidden rounded-2xl border border-card-border bg-white"
        >
          <Skeleton className="h-44 w-full rounded-none" />
          <div className="flex items-end justify-between gap-3 px-4 py-4">
            <div className="flex flex-col gap-2 flex-1">
              <Skeleton className="h-5 w-3/4" />
              <Skeleton className="h-4 w-1/2" />
            </div>
            <Skeleton className="h-9 w-28 shrink-0" />
          </div>
        </div>
      ))}
    </div>
  );
}

/** Renders a 3-column grid of EventCards. Cards are not clickable — only "View Details" navigates. */
function EventGrid({ events, allRegistered = false }: { events: EventCardDto[]; allRegistered?: boolean }) {
  return (
    <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
      {events.map((event) => (
        <EventCard
          key={event.id}
          event={toEventCardProps(event)}
          isRegistered={allRegistered}
          showAccessTag={false}
        />
      ))}
    </div>
  );
}

function FeaturedSection() {
  const { data, isLoading, isError, refetch } = useFeaturedEvent();
  // GET /v1/events/featured is @Public() — no user context on the BE.
  // Derive isRegistered by cross-checking with the registered events list
  // (React Query caches the result so RegisteredEventsSection reuses it).
  const { data: registeredEvents } = useRegisteredEvents();
  const isRegistered = data ? (registeredEvents ?? []).some((e) => e.id === data.id) : false;
  const { mutate: register, isPending: isRegistering } = useRegisterForEvent(data?.id ?? '');
  const [registeredOpen, setRegisteredOpen] = useState(false);

  if (isLoading) return <Skeleton className="h-[20rem] w-full rounded-2xl" />;
  if (isError) return <ErrorState onRetry={() => void refetch()} />;
  if (!data) return null;

  return (
    <section aria-labelledby="featured-heading">
      <h2 id="featured-heading" className="sr-only">Featured Event</h2>
      <FeaturedEventCard
        event={toFeaturedEventProps(data)}
        isRegistered={isRegistered}
        detailPath={eventDetailPath(data.id)}
        onRegister={() => register(undefined, { onSuccess: () => setRegisteredOpen(true) })}
        isRegistering={isRegistering}
      />
      <MemberRegistrationConfirmedDialog
        eventId={data.id}
        open={registeredOpen}
        onClose={() => setRegisteredOpen(false)}
      />
    </section>
  );
}

function RegisteredEventsSection() {
  const { data, isLoading, isError, refetch } = useRegisteredEvents();
  const events = data ?? [];

  if (!isLoading && !isError && events.length === 0) return null;

  return (
    <section aria-labelledby="registered-heading">
      <SectionHeading count={events.length > 0 ? events.length : undefined}>
        <span id="registered-heading">My Registered Events</span>
      </SectionHeading>
      {isLoading && <EventGridSkeleton />}
      {isError && <ErrorState onRetry={() => void refetch()} />}
      {events.length > 0 && <EventGrid events={events} allRegistered />}
    </section>
  );
}

function UpcomingSection() {
  const [page, setPage] = useState(1);
  const { data, isLoading, isError, refetch } = useUpcomingEvents(page);
  const { items: events, total, hasMore } = usePageAccumulation(data, page, 'upcoming');

  return (
    <section aria-labelledby="upcoming-heading">
      <SectionHeading count={total > 0 ? total : undefined}>
        <span id="upcoming-heading">Upcoming Events</span>
      </SectionHeading>

      {isLoading && events.length === 0 && <EventGridSkeleton />}
      {isError && <ErrorState onRetry={() => void refetch()} />}
      {!isError && events.length === 0 && !isLoading && (
        <p className="text-body py-12 text-center">No upcoming events at the moment.</p>
      )}
      {events.length > 0 && <EventGrid events={events} />}

      {hasMore && (
        <div className="mt-8 flex justify-center">
          <Button variant="outline" onClick={() => setPage((p) => p + 1)} disabled={isLoading}>
            {isLoading ? 'Loading…' : 'Load More'}
          </Button>
        </div>
      )}
    </section>
  );
}

function SearchSection({ q, includePast }: { q: string; includePast: boolean }) {
  const [page, setPage] = useState(1);
  const debouncedQ = useDebouncedValue(q, 300);
  const trimmed = debouncedQ.trim();
  const { data, isLoading, isError, refetch } = useSearchEvents(trimmed, includePast, page);
  const resetKey = `${trimmed}::${String(includePast)}`;
  const { items: events, total, hasMore } = usePageAccumulation(data, page, resetKey);

  useEffect(() => { setPage(1); }, [resetKey]);

  if (!trimmed) return null;

  return (
    <section aria-labelledby="search-results-heading">
      <SectionHeading count={total > 0 ? total : undefined}>
        <span id="search-results-heading">Search Results</span>
      </SectionHeading>

      {isLoading && events.length === 0 && <EventGridSkeleton />}
      {isError && <ErrorState onRetry={() => void refetch()} />}
      {!isLoading && !isError && events.length === 0 && (
        <p className="text-body text-center py-12">
          No events found for &ldquo;{debouncedQ}&rdquo;.
        </p>
      )}
      {events.length > 0 && <EventGrid events={events} />}

      {hasMore && (
        <div className="mt-8 flex justify-center">
          <Button variant="outline" onClick={() => setPage((p) => p + 1)} disabled={isLoading}>
            {isLoading ? 'Loading…' : 'Load More'}
          </Button>
        </div>
      )}
    </section>
  );
}

export function EventsPage() {
  const [q, setQ] = useState('');
  const [includePast, setIncludePast] = useState(false);
  const isSearching = q.trim().length > 0;

  return (
    <div className="flex flex-col bg-events-bg min-h-full">
      <PageHeader title="Events" />

      <div className="flex flex-col gap-8 px-page-x py-8">
        {/* 1. Featured event */}
        <FeaturedSection />

        {/* 2. Search bar + checkbox */}
        <div className="flex flex-col gap-2 max-w-[560px]">
          <Input
            label="Search events"
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search events…"
            icon={<SearchIcon />}
          />
          <Checkbox
            label="Include past events"
            checked={includePast}
            onChange={(e) => setIncludePast(e.target.checked)}
          />
        </div>

        {/* 3 & 4. Search replaces registered + upcoming; otherwise show both */}
        {isSearching ? (
          <SearchSection q={q} includePast={includePast} />
        ) : (
          <>
            <RegisteredEventsSection />
            <UpcomingSection />
          </>
        )}
      </div>
    </div>
  );
}
